import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search, Tag, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import ktcLogo from '../assets/ktc.png';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';
import { stripHtml } from '../utils/newsContent';

const API = `${resolveBackendBaseUrl()}/api`;

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await axios.get(`${API}/news`, { params });
      const data = Array.isArray(response.data) ? response.data : [];
      setNews(data);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/news/categories/list`);
      const cats = response.data?.categories;
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, [fetchNews, fetchCategories]);

  const filteredNews = Array.isArray(news) ? news.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    stripHtml(item.content || '').toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const truncateContent = (content, length = 150) => {
    const plain = stripHtml(content || '');
    if (plain.length <= length) return plain;
    return plain.substring(0, length) + '...';
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={ktcLogo} 
                alt="KTC Logo" 
                className="h-10 w-auto"
              />
              <span className="font-outfit font-bold text-lg text-white hidden sm:block">
                Kedai Teknologi Celebes
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="btn-secondary flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Kembali
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-brand-blue/10 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-outfit text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Berita & <span className="gradient-text-red">Informasi</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-400 max-w-2xl mx-auto"
          >
            Dapatkan update terbaru tentang layanan, teknologi, dan promo dari KTC
          </motion.p>
        </div>
      </section>

      {/* Filter & Search */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Cari berita..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#121212] border-white/10 text-white"
                data-testid="news-search-input"
              />
            </div>

            {/* Categories & View Toggle */}
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === '' 
                      ? 'bg-brand-blue text-white' 
                      : 'bg-[#121212] text-gray-400 hover:text-white'
                  }`}
                  data-testid="category-all"
                >
                  Semua
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat 
                        ? 'bg-brand-red text-white' 
                        : 'bg-[#121212] text-gray-400 hover:text-white'
                    }`}
                    data-testid={`category-${cat}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* View Toggle */}
              <div className="flex bg-[#121212] rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded text-sm ${
                    viewMode === 'grid' ? 'bg-brand-blue text-white' : 'text-gray-400'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded text-sm ${
                    viewMode === 'list' ? 'bg-brand-blue text-white' : 'text-gray-400'
                  }`}
                >
                  List
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-gray-400 mt-4">Memuat berita...</p>
            </div>
          ) : filteredNews.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">Belum ada berita tersedia</p>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((item, index) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="service-card bg-[#121212] rounded-xl overflow-hidden group"
                  data-testid={`news-card-${item.id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600'}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-brand-red/90 text-white text-xs font-medium rounded-full">
                      {item.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.author}
                      </span>
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-white mb-3 line-clamp-2 group-hover:text-brand-blue transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                      {truncateContent(item.content)}
                    </p>
                    <Link 
                      to={`/berita/${item.id}`}
                      className="inline-flex items-center gap-2 text-brand-blue text-sm font-medium hover:text-brand-blue-neon transition-colors"
                    >
                      Baca Selengkapnya
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="space-y-6">
              {filteredNews.map((item, index) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="service-card bg-[#121212] rounded-xl overflow-hidden group"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="relative w-full md:w-72 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                      <img 
                        src={item.image_url || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600'}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <span className="absolute top-4 left-4 px-3 py-1 bg-brand-red/90 text-white text-xs font-medium rounded-full md:hidden">
                        {item.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col justify-center">
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                        <span className="hidden md:inline-flex items-center gap-1 px-2 py-1 bg-brand-red/20 text-brand-red rounded">
                          <Tag className="w-3 h-3" />
                          {item.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(item.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {item.author}
                        </span>
                      </div>
                      <h3 className="font-outfit text-xl md:text-2xl font-semibold text-white mb-3 group-hover:text-brand-blue transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 mb-4 line-clamp-2">
                        {truncateContent(item.content, 200)}
                      </p>
                      <Link 
                        to={`/berita/${item.id}`}
                        className="inline-flex items-center gap-2 text-brand-blue font-medium hover:text-brand-blue-neon transition-colors w-fit"
                      >
                        Baca Selengkapnya
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Kedai Teknologi Celebes. Seluruh hak cipta dilindungi.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NewsPage;




